/**
 * Typed bindings for the Inquiries endpoints built in Part A.
 *
 * Lives in `lib/` rather than in a feature folder because TWO features consume it:
 * the dashboard's "needing action" widget and stage-count cards (B3, this work
 * stream) and the pipeline itself (B4, `src/features/inquiries/**`, a different
 * owner). One module means one place where the response shapes are stated, and no
 * chance of two hand-written copies of `InquiryDto` drifting apart.
 *
 * Shapes are transcribed from `apps/api/src/modules/inquiries/inquiries.service.ts`
 * (`InquiryDto`, `InquiryRecordDto`, `StageCountsDto`, `BulkResultDto`) and the
 * list envelope from `modules/countries/support/http.ts` (`Paged<T>`).
 *
 * ONE CONTRACT FACT WORTH REPEATING AT THE CALL SITE: every `BIGINT UNSIGNED`
 * id is a STRING end to end. Above 2^53 a JS number is not the id, so nothing
 * here parses one into a number.
 *
 * ⚠ THE SECOND FACT USED TO BE THE MONEY ONE, AND IT IS GONE WITH ITS FIELD. It
 * read: "`expected_value` is a DECIMAL(14,2) string, and it is MASKED for callers
 * without `amounts.view`. It is typed `string | null` and must never be coerced
 * with `Number()` for display without checking the mask first." Migration 44
 * dropped `inquiries.expected_value` and `inquiries.currency` (owner ruling Q6),
 * so NO shape in this module carries money any more — the inquiry is a pipeline
 * record, not a priced one. The DISCIPLINE the sentence describes is unchanged and
 * still binding everywhere money does ship: `features/inquiries/format.ts` is where
 * it lives, and `crfs`, `quotes`, `renewals` and the catalogue all go through it.
 */

import { apiRequest } from './api';
import type { InquiryStage } from '../domain/stages';

export interface Paged<T> {
  data: T[];
  page: number;
  perPage: number;
  total: number;
}

/*
 * ⚠ `InquiryChannel` USED TO BE DECLARED HERE, AND ITS ABSENCE IS THE POINT.
 *
 * `inquiries.channel` is dropped by migration 43 (owner ruling Q5 — "How the customer
 * reached us" is removed from the inquiry). The column is gone, so the union that
 * mirrored it is gone, and so is `features/inquiries/channels.ts`, which held the browser's
 * checked copy of the API ENUM together with the labels.
 *
 * Not to be confused, when grepping, with `notifications.channel` (`inapp`/`email`/`push`,
 * still very much alive on a different table) or with how a QUOTE was delivered
 * (`SendQuoteDto.channel`). Neither is this field.
 */
export type FollowupKind = 'followup' | 'extra_call';

/**
 * `inquiries.tally_edition` — migration 45, owner ruling Q9.
 *
 * A CHECKED COPY of the API ENUM (`TALLY_EDITIONS` in `entities/inquiry.entity.ts`), the
 * same treatment `FollowupKind` gets one line up. Two members because there are two things
 * a customer can say; `null` is the third state and it is the ordinary one.
 *
 * ⚠ THIS IS NOT A MIRROR OF THE LICENCE REGISTER, because the register has no edition
 * column. There is nothing here that can drift out of step with a second list — the whole
 * point of ruling Q9 (`A only`) is that this fact is recorded nowhere else.
 */
export type TallyEdition = 'single' | 'multi';

/**
 * One value from the SHARED PLATFORM VOCABULARY as an inquiry carries it — the API's
 * `InquiryPlatformView`, transcribed (migration 48, owner ruling 5: *"One list, three
 * applicability words: crf, project, inquiry."*). The CRF record's `CrfPlatformDto` has
 * the same three fields, so one component can render both attach surfaces.
 *
 * ⚠ `name` COMES FROM THE SERVER. The vocabulary is a table the owner edits without a
 * release, and nothing here looks a label up — a value he has retired still arrives with
 * its name, so a record that carries it still reads correctly.
 *
 * `detail` is `null` on every LIST row and the text on the RECORD (the API's own split: the
 * list cell shows WHICH values a row carries, never the scope paragraphs).
 */
export interface InquiryPlatformDto {
  platform_id: string;
  name: string;
  detail: string | null;
}

export interface InquiryDto {
  id: string;
  doc_no: string;
  legacy_ref: string | null;
  country_id: number;
  party_id: string;
  /** Projected from the joined `parties` row — the customer shown on every row. */
  party_name: string | null;
  subject: string | null;
  details: string | null;
  stage: InquiryStage;
  /** ISO-8601. The days-in-stage chip and the overdue accent come from this. */
  stage_entered_at: string | null;
  assigned_to: string | null;
  assigned_to_name: string | null;
  /**
   * WHAT THE CUSTOMER TOLD US — migration 45, owner ruling Q9. A CLAIM, not a lookup.
   *
   * Nothing on the server checks these against `tally_licences`, so a non-null
   * `tally_serial` here is not evidence that the serial exists or belongs to this
   * customer. Every renderer of these three has to say so; `InquiryRecordPage`'s group
   * carries the sentence.
   *
   * ⚠ AND THEY ARE NEVER MASKED. None of the three is in the API's `MONEY_FIELDS`, so
   * `isMaskedAmount` has nothing to do with them and no seat sees `•••` here. A serial is
   * not a price.
   */
  tally_serial: string | null;
  tally_edition: TallyEdition | null;
  tally_release: string | null;
  /*
   * ⚠ `expected_value` AND `currency` LEFT THIS SHAPE WITH THEIR COLUMNS (migration 44,
   * owner ruling Q6). Transcribed from `InquiryDto` in the API service, which no longer
   * declares them either — the two shapes stay one shape by being edited together, which
   * is the whole reason this module exists in `lib/` rather than twice in feature folders.
   */
  lost_reason: string | null;
  /**
   * The shared-vocabulary values this inquiry carries (migration 48, owner ruling 5), in
   * the VOCABULARY's order. ALWAYS PRESENT — `[]` when none — transcribed from the API's
   * `InquiryDto.platforms`, so no renderer has to tell "no key" from "no values". This is
   * the field the design's MODULES column and REQUIREMENTS group draw from; the two
   * refusals that stood in `InquiryTable.tsx` and `VISUAL-HONESTY.md` are answered by it.
   */
  platforms: InquiryPlatformDto[];
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface FollowupDto {
  id: string;
  inquiry_id: string;
  country_id: number;
  kind: FollowupKind;
  note: string;
  next_action_at: string | null;
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** `GET /inquiries/:id` — the record with its follow-up timeline nested. */
export interface InquiryRecordDto extends InquiryDto {
  followups: FollowupDto[];
}

/**
 * `GET /countries/:cc/inquiries/stage-counts`.
 *
 * `counts` carries EVERY member of the stage ENUM including the zeros — the API
 * fills them in deliberately, so a quiet stage does not vanish from the UI.
 */
export interface StageCountsDto {
  country_id: number;
  counts: Record<InquiryStage, number>;
  total: number;
}

export interface InquiryListParams {
  /** A stage, or a comma-separated list. An unparseable value is a 422, not a silent all-rows. */
  stage?: string;
  /** A user id, or the literal `none`. */
  assigned?: string;
  /** Matched against inquiry subject and party name. */
  q?: string;
  page?: number;
  perPage?: number;
}

export async function fetchInquiries(
  countryCode: string,
  params: InquiryListParams = {},
  signal?: AbortSignal,
): Promise<Paged<InquiryDto>> {
  return apiRequest<Paged<InquiryDto>>(`/countries/${countryCode}/inquiries`, {
    query: {
      stage: params.stage,
      assigned: params.assigned,
      q: params.q,
      page: params.page,
      perPage: params.perPage,
    },
    signal,
  });
}

export async function fetchStageCounts(
  countryCode: string,
  signal?: AbortSignal,
): Promise<StageCountsDto> {
  return apiRequest<StageCountsDto>(`/countries/${countryCode}/inquiries/stage-counts`, {
    signal,
  });
}

/** Query keys, in one place so the dashboard and the pipeline invalidate the same cache. */
export const inquiryKeys = {
  all: (cc: string) => ['inquiries', cc] as const,
  list: (cc: string, params: InquiryListParams) => ['inquiries', cc, 'list', params] as const,
  stageCounts: (cc: string) => ['inquiries', cc, 'stage-counts'] as const,
  record: (id: string) => ['inquiry', id] as const,
  /**
   * The create screen's platform picker (`GET /countries/:cc/inquiries/platforms`). Under
   * the country's `['inquiries', cc]` prefix on purpose: a create invalidates that prefix,
   * and re-reading the picker after a create costs one small request and can only make it
   * more current (a value the owner widened meanwhile shows up without a reload).
   */
  platformOptions: (cc: string) => ['inquiries', cc, 'platform-options'] as const,
};
